<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="960"
    scrollable
    persistent
  >
    <v-card class="rounded-xl">
      <v-card-title class="bg-primary text-white py-4 px-6 d-flex align-center">
        <v-icon icon="mdi-filter-variant" class="me-2" />
        {{ t('filtersettings.title') }}
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" color="white" @click="closeDialog" />
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-2">{{ t('filtersettings.filtersApplied') }}</div>
          <div v-if="appliedDraftChips.length" class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="chip in appliedDraftChips"
              :key="`${chip.field}-${chip.label}`"
              closable
              color="primary"
              variant="tonal"
              @click:close="clearField(chip.field)"
            >
              {{ chip.label }}
            </v-chip>
          </div>
          <v-alert v-else type="info" variant="tonal" density="compact" class="mb-0">
            {{ t('filtersettings.noFiltersSelected') }}
          </v-alert>
        </div>

        <v-divider class="mb-4" />

        <v-alert
          v-if="validationMessage"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          closable
          @click:close="validationMessage = ''"
        >
          {{ validationMessage }}
        </v-alert>

        <section
          v-for="section in panelSections"
          :key="section.key"
          class="mb-6"
        >
          <div class="text-h6 font-weight-bold mb-3">{{ t(section.title) }}</div>
          <v-alert
            v-if="section.key === 'attributes' && section.fields.length === 0"
            type="info"
            variant="tonal"
            density="compact"
          >
            {{ t('filtersettings.noCustomAttributes') }}
          </v-alert>

          <template v-for="field in section.fields" :key="field.key">
          <div
            v-if="draft[field.key]"
            class="filter-row d-flex align-start ga-3 mb-4"
          >
            <div class="operator-column pt-2">
              <v-radio-group
                v-model="draft[field.key].operator"
                density="compact"
                hide-details
                inline
                class="operator-group"
              >
                <v-radio :label="t('filtersettings.or')" value="or" />
                <v-radio :label="t('filtersettings.and')" value="and" />
              </v-radio-group>
            </div>

            <div class="field-column flex-grow-1">
              <div class="text-subtitle-2 font-weight-medium mb-2">{{ t(field.label) }}</div>

              <div v-if="field.type === 'range'" class="px-2">
                <v-range-slider
                  v-model="draft[field.key].values"
                  :min="field.min"
                  :max="field.max"
                  step="1"
                  thumb-label
                  color="primary"
                  hide-details
                  class="mt-2"
                />
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ formatRangeCaption(field) }}
                </div>
              </div>

              <div v-else-if="field.type === 'income_range'" class="income-range-wrap">
                <div class="income-selected-range">
                  <span class="income-selected-label">{{ t('filtersettings.selectedRange') }}</span>
                  <div class="d-flex align-center flex-wrap ga-2 mt-1">
                    <v-chip color="primary" variant="tonal" class="income-range-chip text-capitalize">
                      {{ incomeLevelLabel(draft[field.key].values[0]) }}
                    </v-chip>
                    <span class="income-range-separator">{{ t('filtersettings.to') }}</span>
                    <v-chip color="primary" variant="tonal" class="income-range-chip text-capitalize">
                      {{ incomeLevelLabel(draft[field.key].values[1]) }}
                    </v-chip>
                  </div>
                </div>

                <v-range-slider
                  v-model="draft[field.key].values"
                  :min="0"
                  :max="incomeMaxIndex"
                  step="1"
                  color="primary"
                  hide-details
                  class="income-range-slider"
                />

                <div class="income-ticks">
                  <span
                    v-for="(level, index) in INCOME_LEVELS"
                    :key="level"
                    class="income-tick-label text-capitalize"
                    :class="{
                      'income-tick-label--active': isIncomeTickActive(field.key, index),
                    }"
                  >
                    {{ filterOptionLabel(level) }}
                  </span>
                </div>
              </div>

              <v-text-field
                v-else-if="field.type === 'text'"
                v-model="draft[field.key].values[0]"
                variant="outlined"
                density="comfortable"
                hide-details
                clearable
                :placeholder="textFieldPlaceholder(field)"
              />

              <v-btn-toggle
                v-else-if="field.type === 'toggle'"
                v-model="draft[field.key].values"
                multiple
                divided
                color="primary"
                variant="outlined"
                density="comfortable"
                class="flex-wrap"
              >
                <v-btn
                  v-for="option in (field.options || optionLists[field.key] || [])"
                  :key="option"
                  :value="option"
                  class="text-capitalize"
                >
                  {{ filterOptionLabel(option) }}
                </v-btn>
              </v-btn-toggle>

              <v-autocomplete
                v-else-if="field.type === 'multiselect'"
                v-model="draft[field.key].values"
                :items="optionLists[field.key] || []"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="comfortable"
                hide-details
                :placeholder="t('filtersettings.selectField', { field: t(field.label) })"
              />

              <v-autocomplete
                v-else-if="field.type === 'country_select'"
                v-model="draft[field.key].values"
                v-model:search="countrySearch"
                :items="countryAutocompleteItems"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="comfortable"
                hide-details
                :placeholder="t('filtersettings.selectOrSearchCountry')"
                @update:model-value="onCountryChange"
              />

              <v-combobox
                v-else-if="field.type === 'tags'"
                v-model="draft[field.key].values"
                :items="optionLists[field.key] || []"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="comfortable"
                hide-details
                :placeholder="t('filtersettings.addField', { field: t(field.label) })"
              />
            </div>
          </div>
          </template>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4 px-6 d-flex flex-wrap ga-3">
        <div class="text-body-2 font-weight-medium">
          {{ t('filtersettings.matchingProfilesFound', { count: matchCount }, matchCount) }}
        </div>
        <v-spacer />
        <v-btn variant="text" @click="openLoadDialog">{{ t('filtersettings.load') }}</v-btn>
        <v-btn variant="text" @click="openSaveDialog">{{ t('generic.save') }}</v-btn>
        <v-btn variant="text" @click="clearAll">{{ t('filtersettings.clearAll') }}</v-btn>
        <v-btn color="primary" variant="flat" @click="applyFilters">{{ t('filtersettings.applyFilters') }}</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="saveDialogOpen" max-width="480">
      <v-card class="rounded-xl">
        <v-card-title class="bg-primary text-white py-4">{{ t('filtersettings.saveFilterTitle') }}</v-card-title>
        <v-card-text class="pa-6">
          <v-text-field
            v-model="saveFilterName"
            :label="t('filtersettings.filterNameLabel')"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" @click="saveDialogOpen = false">{{ t('generic.cancel') }}</v-btn>
          <v-btn color="primary" variant="flat" :disabled="!saveFilterName.trim()" @click="confirmSave">
            {{ t('generic.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadDialogOpen" max-width="560">
      <v-card class="rounded-xl">
        <v-card-title class="bg-primary text-white py-4">{{ t('filtersettings.loadFilterTitle') }}</v-card-title>
        <v-card-text class="pa-4">
          <v-alert v-if="savedPresets.length === 0" type="info" variant="tonal" density="compact">
            {{ t('filtersettings.noSavedFilters') }}
          </v-alert>
          <v-list v-else lines="two" density="comfortable">
            <v-list-item
              v-for="preset in savedPresets"
              :key="preset.id"
              :title="preset.name"
              :subtitle="formatSavedAt(preset.savedAt)"
            >
              <template #append>
                <v-btn icon="mdi-upload" variant="text" color="primary" @click="loadPreset(preset)" />
                <v-btn icon="mdi-delete-outline" variant="text" color="error" @click="removePreset(preset.id)" />
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" @click="loadDialogOpen = false">{{ t('generic.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { fetch_persona_domain_schema } from '@/api.js';
import { getFilterOptionLabel, INCOME_LEVELS, formatNumberRangeLabel } from '@/constants/filterSchema';
import { mergeSelectChoicesWithValue } from '@/utils/attributeValueCoercion';
import {
  buildDefaultFilterName,
  deleteFilterPreset,
  loadFilterPresets,
  saveFilterPreset,
} from '@/utils/filterStorage';
import { filterMasterCountries } from '@/utils/countryList';
import { formatDateTime } from '@/utils/dateFormat';
import {
  applyCriteriaToDraft,
  buildAvailableCountryOptions,
  collectDistinctOptions,
  countMatchingPersonas,
  countMatchingChatPersonaOptions,
  createEmptyDraft,
  draftToCriteria,
  formatCriterionLabel,
  formatDroppedFilterFieldsMessage,
  getAllFilterFields,
  getPanelSections,
  hasActiveCriteria,
  isValidDraftEntry,
  normalizeDraftEntryForField,
  reconcileFilterCriteria,
  sanitizeCountryValues,
} from '@/utils/personaFilter';

export default {
  name: 'FilterSettingsDialog',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    domain: {
      type: String,
      default: '',
    },
    personas: {
      type: Array,
      default: () => [],
    },
    generatedPersonas: {
      type: Array,
      default: () => [],
    },
    chatApiPersonas: {
      type: Array,
      default: null,
    },
    isGenerated: {
      type: Boolean,
      default: false,
    },
    appliedCriteria: {
      type: Array,
      default: () => [],
    },
    schemaAttributes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'apply', 'clear'],
  data() {
    return {
      INCOME_LEVELS,
      draft: {},
      saveDialogOpen: false,
      loadDialogOpen: false,
      saveFilterName: '',
      savedPresets: [],
      validationMessage: '',
      countrySearch: '',
      schemaAttributes: [],
    };
  },
  computed: {
    personaCountryOptions() {
      return buildAvailableCountryOptions(this.personas, this.isGenerated);
    },
    countryAutocompleteItems() {
      const search = this.countrySearch.trim();
      if (!search) {
        return this.personaCountryOptions;
      }
      return filterMasterCountries(search);
    },
    sanitizedDraft() {
      const draft = JSON.parse(JSON.stringify(this.draft));
      if (this.isCountrySelectField && draft.country?.values) {
        draft.country.values = sanitizeCountryValues(draft.country.values).sanitized;
      }
      return draft;
    },
    allFields() {
      return getAllFilterFields(
        this.personas,
        this.isGenerated,
        this.schemaAttributes,
      );
    },
    panelSections() {
      return getPanelSections(this.allFields);
    },
    optionLists() {
      return this.allFields.reduce((acc, field) => {
        if (field.type === 'country_select') {
          acc[field.key] = this.personaCountryOptions;
        } else if (['multiselect', 'tags', 'toggle'].includes(field.type)) {
          // Schema choices are authoritative, but merge in values already
          // stored on personas so legacy data remains filterable after a type
          // or choice-list change in the Framework Editor.
          const discovered = collectDistinctOptions(this.personas, field.key, this.isGenerated);
          if (Array.isArray(field.options) && field.options.length) {
            acc[field.key] = mergeSelectChoicesWithValue(field.options, discovered);
          } else {
            acc[field.key] = discovered;
          }
        }
        return acc;
      }, {});
    },
    draftCriteria() {
      return draftToCriteria(this.sanitizedDraft, this.allFields);
    },
    appliedDraftChips() {
      return this.draftCriteria.map((criterion) => ({
        field: criterion.field,
        label: formatCriterionLabel(criterion, this.allFields, this.t),
      }));
    },
    matchCount() {
      if (Array.isArray(this.chatApiPersonas)) {
        const staticFields = getAllFilterFields(
          this.personas,
          false,
          this.schemaAttributes,
        );
        const generatedFields = getAllFilterFields(
          this.generatedPersonas,
          true,
          this.schemaAttributes,
        );
        const hasCriteria =
          hasActiveCriteria(this.draftCriteria, staticFields)
          || hasActiveCriteria(this.draftCriteria, generatedFields);

        if (!hasCriteria) {
          return this.chatApiPersonas.length;
        }

        return countMatchingChatPersonaOptions(this.chatApiPersonas, {
          staticPersonas: this.personas,
          generatedPersonas: this.generatedPersonas,
          criteria: this.draftCriteria,
          schemaAttributes: this.schemaAttributes,
        });
      }

      if (!hasActiveCriteria(this.draftCriteria, this.allFields)) {
        return this.personas.length;
      }
      return countMatchingPersonas(
        this.personas,
        this.draftCriteria,
        this.isGenerated,
        this.allFields,
      );
    },
    incomeMaxIndex() {
      return INCOME_LEVELS.length - 1;
    },
  },
  watch: {
    async modelValue(open) {
      if (open) {
        this.validationMessage = '';
        this.countrySearch = '';
        await this.loadDomainSchema();
        this.resetDraftFromApplied();
        this.refreshSavedPresets();
      }
    },
    async domain() {
      if (this.modelValue) {
        await this.loadDomainSchema();
        this.resetDraftFromApplied();
      }
    },
    appliedCriteria: {
      deep: true,
      handler() {
        if (this.modelValue) {
          this.resetDraftFromApplied();
        }
      },
    },
    allFields: {
      handler() {
        if (!this.modelValue) return;
        this.syncDraftWithFields();
      },
    },
    personas: {
      deep: true,
      handler() {
        if (!this.modelValue) return;
        this.syncDraftWithFields();
      },
    },
    // Schema can arrive asynchronously after the dialog is already open
    // (e.g. a slow persona-schema fetch) — seed draft entries for any
    // newly-available schema-driven field the same way the personas
    // watcher already does for data-discovered fields.
    schemaAttributes: {
      deep: true,
      handler() {
        if (!this.modelValue) return;
        this.syncDraftWithFields();
      },
    },
  },
  methods: {
    async loadDomainSchema() {
      if (!this.domain) {
        this.schemaAttributes = [];
        return;
      }
      try {
        const response = await fetch_persona_domain_schema(this.domain);
        this.schemaAttributes = response?.data?.attributes || [];
      } catch {
        // Schema is optional for filters — fall back to distinct persona values.
        this.schemaAttributes = [];
      }
    },
    syncDraftWithFields() {
      const emptyDraft = createEmptyDraft(this.allFields);
      const fieldsByKey = Object.fromEntries(this.allFields.map((field) => [field.key, field]));

      Object.entries(emptyDraft).forEach(([key, value]) => {
        const field = fieldsByKey[key];
        if (!this.draft[key]) {
          this.draft[key] = { ...value };
          return;
        }
        if (field && !isValidDraftEntry(field, this.draft[key])) {
          this.draft[key] = normalizeDraftEntryForField(field, this.draft[key]);
        }
      });

      const validKeys = new Set(this.allFields.map((field) => field.key));
      Object.keys(this.draft).forEach((key) => {
        if (!validKeys.has(key)) {
          delete this.draft[key];
        }
      });
    },
    onCountryChange(values) {
      if (!this.draft.country) return;

      const { sanitized, rejected } = sanitizeCountryValues(values);
      this.draft.country.values = sanitized;
      this.countrySearch = '';

      if (rejected.length) {
        this.validationMessage = this.buildInvalidCountryMessage(rejected);
      } else if (this.validationMessage) {
        this.validationMessage = '';
      }
    },
    buildInvalidCountryMessage(rejected) {
      return this.t('filtersettings.invalidCountryError', { countries: rejected.join('", "') });
    },
    getInvalidCountries() {
      if (!this.isCountrySelectField || !this.draft.country?.values?.length) {
        return [];
      }
      return sanitizeCountryValues(this.draft.country.values).rejected;
    },
    sanitizeCountryDraft(silent = false) {
      if (!this.isCountrySelectField || !this.draft.country) return true;

      const { sanitized, rejected } = sanitizeCountryValues(this.draft.country.values);
      this.draft.country.values = sanitized;

      if (!silent && rejected.length) {
        this.validationMessage = this.buildInvalidCountryMessage(rejected);
        return false;
      }

      return true;
    },
    resetDraftFromApplied() {
      const { criteria, droppedFields } = reconcileFilterCriteria(
        this.appliedCriteria,
        this.allFields,
        this.schemaAttributes,
      );
      this.draft = applyCriteriaToDraft(criteria, this.allFields);
      this.sanitizeCountryDraft(true);
      this.validationMessage = droppedFields.length
        ? formatDroppedFilterFieldsMessage(droppedFields, this.t)
        : '';
    },
    incomeLevelLabel(index) {
      return getFilterOptionLabel(this.t, INCOME_LEVELS[index]) || String(index);
    },
    // The pre-existing name_search field keeps its exact original
    // placeholder text unchanged; any other type: 'text' field (a
    // schema-driven text_short custom attribute) gets a generic
    // "Search {field}" placeholder instead of the name-specific one.
    textFieldPlaceholder(field) {
      return field.key === 'name_search'
        ? this.t('filtersettings.searchByPersonaName')
        : this.t('filtersettings.searchField', { field: this.t(field.label) });
    },
    // Translates a known predefined filter option (income level, gender,
    // civil status) via the i18n key lookup in filterSchema.js. Falls back
    // to the raw value unchanged for anything not predefined there.
    filterOptionLabel(value) {
      return getFilterOptionLabel(this.t, value);
    },
    formatRangeCaption(field) {
      const values = this.draft[field.key]?.values || [];
      return formatNumberRangeLabel(values[0], values[1], {
        unit: field.unit,
        fieldKey: field.key,
        t: this.t,
      });
    },
    isIncomeTickActive(fieldKey, index) {
      const [minIndex, maxIndex] = this.draft[fieldKey]?.values || [0, this.incomeMaxIndex];
      return index >= minIndex && index <= maxIndex;
    },
    clearField(fieldKey) {
      const emptyDraft = createEmptyDraft(this.allFields);
      if (!emptyDraft[fieldKey]) return;
      this.draft[fieldKey] = { ...emptyDraft[fieldKey] };
    },
    clearAll() {
      this.draft = createEmptyDraft(this.allFields);
      this.$emit('clear');
      this.$emit('apply', []);
      this.closeDialog();
    },
    applyFilters() {
      const invalidCountries = this.getInvalidCountries();
      if (invalidCountries.length) {
        this.validationMessage = this.buildInvalidCountryMessage(invalidCountries);
        return;
      }

      this.validationMessage = '';
      this.$emit('apply', this.draftCriteria);
      this.closeDialog();
    },
    closeDialog() {
      this.$emit('update:modelValue', false);
    },
    openSaveDialog() {
      this.saveFilterName = buildDefaultFilterName(this.domain);
      this.saveDialogOpen = true;
    },
    confirmSave() {
      const name = this.saveFilterName.trim();
      if (!name) return;

      const invalidCountries = this.getInvalidCountries();
      if (invalidCountries.length) {
        this.validationMessage = this.buildInvalidCountryMessage(invalidCountries);
        return;
      }

      this.validationMessage = '';
      saveFilterPreset(this.domain, name, this.draftCriteria);
      this.refreshSavedPresets();
      this.saveDialogOpen = false;
    },
    openLoadDialog() {
      this.refreshSavedPresets();
      this.loadDialogOpen = true;
    },
    loadPreset(preset) {
      const { criteria, droppedFields } = reconcileFilterCriteria(
        preset.criteria || [],
        this.allFields,
        this.schemaAttributes,
      );
      this.draft = applyCriteriaToDraft(criteria, this.allFields);
      this.sanitizeCountryDraft(true);
      this.validationMessage = droppedFields.length
        ? formatDroppedFilterFieldsMessage(droppedFields, this.t)
        : '';
      this.loadDialogOpen = false;
    },
    removePreset(presetId) {
      this.savedPresets = deleteFilterPreset(this.domain, presetId);
    },
    refreshSavedPresets() {
      this.savedPresets = loadFilterPresets(this.domain);
    },
    formatSavedAt(value) {
      if (!value) return '';
      try {
        return formatDateTime(value, this.$i18n.locale);
      } catch {
        return value;
      }
    },
  },
};
</script>

<style scoped>
.filter-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding-bottom: 12px;
}

.operator-column {
  width: 110px;
  flex-shrink: 0;
}

.operator-group :deep(.v-selection-control-group) {
  flex-direction: column;
  align-items: flex-start;
}

.field-column {
  min-width: 0;
}

.income-range-wrap {
  padding: 4px 8px 8px;
}

.income-selected-range {
  margin-bottom: 12px;
}

.income-selected-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
}

.income-range-chip {
  font-size: 0.9375rem;
  font-weight: 600;
  height: auto;
  min-height: 32px;
  padding: 8px 14px;
}

.income-range-separator {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.income-range-slider {
  margin-top: 4px;
}

.income-ticks {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 4px;
  margin-top: 10px;
  padding: 0 2px;
}

.income-tick-label {
  text-align: center;
  font-size: 0.8125rem;
  line-height: 1.25;
  color: rgba(0, 0, 0, 0.55);
  word-break: break-word;
}

.income-tick-label--active {
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
}
</style>
